/* eslint-disable @typescript-eslint/triple-slash-reference */
/// <reference path="./IntlTelInputWrapper.d.ts" />
import React, { useCallback, useEffect, useRef } from 'react';
import type { IntlTelInputRef } from 'intl-tel-input/reactWithUtils';
import type IntlTelInputType from 'intl-tel-input/reactWithUtils';
import loadable from '@loadable/component';
import 'intl-tel-input/build/css/intlTelInput.css';

export type { IntlTelInputRef };

type IntlTelInputProps = React.ComponentPropsWithoutRef<typeof IntlTelInputType>;

type Props = Readonly<
  Omit<IntlTelInputProps, 'onChangeNumber' | 'onChangeValidity' | 'initialValue'> & {
    readonly initialValue?: string | null;
    readonly onChangeNumber?: (number: string) => void;
    readonly onChangeValidity?: (valid: boolean) => void;
    // Change this value to reset the input (cannot use React key due to intl-tel-input DOM manipulation)
    readonly resetKey?: React.Key | null;
  }
>;

const IntlTelInputLoadable = loadable(
  () =>
    import(
      /* webpackChunkName: "static/webpack/vendor/intl-tel-input/reactWithUtils" */
      'intl-tel-input/reactWithUtils'
    ),
  {
    ssr: false,
  },
);

export const IntlTelInputWrapper: React.FC<Props> = ({ initialValue, onChangeNumber, onChangeValidity, resetKey, ...passThroughProps }) => {
  const inputRef = useRef<IntlTelInputRef>(null);
  const initialValueRef = useRef<typeof initialValue>(initialValue);

  // Track last values to prevent infinite loops from IntlTelInput firing on every render
  const lastPhoneNumberRef = useRef<string | undefined>(undefined);
  const lastValidityRef = useRef<boolean | undefined>(undefined);
  const onChangeNumberRef = useRef(onChangeNumber);
  const onChangeValidityRef = useRef(onChangeValidity);

  // Keep refs fresh without re-creating callbacks
  onChangeNumberRef.current = onChangeNumber;
  onChangeValidityRef.current = onChangeValidity;
  initialValueRef.current = initialValue;

  // Reset input when resetKey changes (intl-tel-input manipulates DOM, so we must reset imperatively)
  useEffect(() => {
    const resetValue = initialValueRef.current ?? '';
    // Set lastPhoneNumberRef to the reset value BEFORE calling setNumber
    // This prevents the onChangeNumber callback from triggering formik updates
    // which could interfere with other instances' reset effects
    lastPhoneNumberRef.current = resetValue;
    lastValidityRef.current = undefined;
    inputRef.current?.getInstance()?.setNumber(resetValue);
  }, [resetKey]);

  const handleChangeNumber = useCallback((number: string) => {
    if (lastPhoneNumberRef.current !== number) {
      lastPhoneNumberRef.current = number;
      onChangeNumberRef.current?.(number);
    }
  }, []);

  const handleChangeValidity = useCallback((valid: boolean) => {
    if (lastValidityRef.current !== valid) {
      lastValidityRef.current = valid;
      onChangeValidityRef.current?.(valid);
    }
  }, []);

  return (
    <IntlTelInputLoadable
      ref={inputRef}
      initialValue={initialValue ?? undefined}
      onChangeNumber={handleChangeNumber}
      onChangeValidity={handleChangeValidity}
      {...passThroughProps}
    />
  );
};
